import type { FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import lowerCase from "lodash-es/lowerCase"
import map from "lodash-es/map"
import startCase from "lodash-es/startCase"

import {
    EHorizontalProgressBarVariant,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    HorizontalProgressBar,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { getCssIdentifierFromFileName, renderRichContent } from "@/3514/utils"
import {
    type IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"

import type {
    IDiagnosticSummarySummaryModel as ISummaryModel,
    IDiagnosticSummarySummaryModuleModel as ISummaryModuleModel
} from "./diagnostic-summary.api.types"
import classes from "./diagnostic-summary.styles"
import uiConfig from "./diagnostic-summary.ui.config"

const displayName: string = "DiagnosticSummaryModule"
const editorCssIdentifier: string = getCssIdentifierFromFileName(displayName, "ed")

type TModuleProps = { summary: ISummaryModel }
const Module: FC<TModuleProps> = ({ summary }: TModuleProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const getValueFromTranslatedObjectFunction: TGetValueFromTranslatedObjectFunction =
        useGetValueFromTranslatedObject()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    return (
        <div className={classes.modules.item()}>
            <Typography
                text={t("entities.diagnosticSummary.modules.title", {
                    title: getValueFromTranslatedObjectFunction(summary.category.name)
                })}
                tag={ETypographyTag.Heading6}
                color={ETypographyColor.Black}
                size={ETypographySize.Medium}
                family={ETypographyFamily.DmSerif}
                weight={ETypographyWeight.Medium}
                className={classes.modules.title()}
            />

            <div className={classes.modules.list()} hidden={isEmpty(summary.modules)}>
                {map<ISummaryModuleModel, ReactElement>(summary.modules, (m: ISummaryModuleModel): ReactElement => {
                    const _moduleTitle: string = getValueFromTranslatedObjectFunction(m.title)
                    const _moduleSummary: string = getValueFromTranslatedObjectFunction(m.summary.shortSummary)
                    const _moduleText: string = getValueFromTranslatedObjectFunction(m.summary.summary)

                    return (
                        <div key={`m-c-m-${m.id}`} className={classes.modules.listItem()} hidden={isEmpty(m.summary)}>
                            <div>
                                <div hidden={isEmpty(_moduleTitle)}>
                                    <Typography
                                        text={_moduleTitle}
                                        tag={ETypographyTag.Paragraph}
                                        color={ETypographyColor.Black}
                                        size={isMobile ? ETypographySize.Small : ETypographySize.Medium}
                                        family={ETypographyFamily.Nunito}
                                        weight={ETypographyWeight.Bold}
                                    />
                                </div>

                                <div className={classes.modules.listItemFeature()} hidden={isEmpty(_moduleSummary)}>
                                    <HorizontalProgressBar
                                        isActive
                                        progress={(m.rank * 100) / m.maxRank}
                                        variant={EHorizontalProgressBarVariant.Light}
                                        height="5px"
                                        width="90px"
                                    />

                                    <Typography
                                        text={startCase(lowerCase(_moduleSummary))}
                                        tag={ETypographyTag.Paragraph}
                                        color={ETypographyColor.Dark}
                                        size={ETypographySize.Tiny}
                                        family={ETypographyFamily.Nunito}
                                        weight={ETypographyWeight.Default}
                                    />
                                </div>
                            </div>

                            <div hidden={isEmpty(_moduleText)}>
                                {richTextRendererUtils.isStringPlainText(_moduleText) ? (
                                    <Typography
                                        text={_moduleText}
                                        tag={ETypographyTag.Paragraph}
                                        color={ETypographyColor.Dark}
                                        size={isMobile ? ETypographySize.Small : ETypographySize.Medium}
                                        family={ETypographyFamily.Nunito}
                                        weight={ETypographyWeight.Default}
                                    />
                                ) : (
                                    renderRichContent({
                                        identifier: editorCssIdentifier,
                                        classes: classes.modules.getEditorInsertableClasses(editorCssIdentifier),
                                        allowedTags: uiConfig.editorAllowedTags
                                    })(_moduleText)
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

Module.displayName = displayName

export default Module
